import { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Calendar, Clock, Tag as TagIcon } from "lucide-react";
import { useBlogPosts, useCaseStudies } from "@/data/hooks";

/**
 * Curated content carousel driven by tags.
 *
 * Props:
 *   tag          - string. Tag to filter blog posts by (e.g. "Advance ERP").
 *   source       - "blog" (default) | "case-studies"
 *   title        - section title (default "Related from the Blog")
 *   subtitle     - optional supporting copy
 *   excludeSlug  - hide a specific slug (useful on detail pages)
 *   limit        - max items shown (default 12)
 *   testId       - root data-testid prefix
 */
export default function RelatedContentCarousel({
  tag,
  source = "blog",
  title = "Related from the Blog",
  subtitle,
  excludeSlug,
  limit = 12,
  testId = "related-carousel",
}) {
  const { posts: blogPosts } = useBlogPosts();
  const { caseStudies } = useCaseStudies();

  const items = useMemo(() => {
    if (source === "case-studies") {
      return caseStudies
        .filter((c) => (excludeSlug ? c.slug !== excludeSlug : true))
        .slice(0, limit)
        .map((c) => ({
          key: c.slug,
          to: `/case-studies/${c.slug}`,
          image: c.image,
          tag: c.industry,
          title: c.headline,
          excerpt: c.excerpt,
          metric: c.metric,
          metricLabel: c.metricLabel,
          author: c.published ? null : "Coming soon",
          date: null,
          readTime: null,
          published: c.published,
        }));
    }
    return blogPosts
      .filter((p) => (tag ? p.tags?.includes(tag) : true))
      .filter((p) => (excludeSlug ? p.slug !== excludeSlug : true))
      .slice(0, limit)
      .map((p) => ({
        key: p.slug,
        to: `/blog/${p.slug}`,
        image: p.image,
        tag: p.category,
        title: p.title,
        excerpt: p.excerpt,
        author: p.author,
        date: p.date,
        readTime: p.readTime,
        published: true,
      }));
  }, [tag, source, excludeSlug, limit, blogPosts, caseStudies]);

  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items]);

  if (!items.length) return null;

  const isMany = items.length > 3;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 720), behavior: "smooth" });
  };

  return (
    <section data-testid={testId} className="py-14 lg:py-20 bg-slate-50/60 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7 lg:mb-9">
          <div>
            <p className="text-[11px] font-semibold text-sky-600 uppercase tracking-widest mb-1.5">
              {tag || (source === "case-studies" ? "Customer Stories" : "Antera Insights")}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
            {subtitle && <p className="mt-2 text-sm lg:text-base text-slate-500 max-w-2xl">{subtitle}</p>}
          </div>

          {isMany && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                disabled={!canLeft}
                data-testid={`${testId}-prev`}
                aria-label="Scroll left"
                className="w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:text-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                disabled={!canRight}
                data-testid={`${testId}-next`}
                aria-label="Scroll right"
                className="w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:text-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Grid for ≤ 3, swipeable scroller for > 3 */}
        {isMany ? (
          <div
            ref={scrollerRef}
            data-testid={`${testId}-scroller`}
            className="flex gap-4 lg:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {items.map((it, i) => (
              <CarouselCard key={it.key} item={it} index={i} testId={testId} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {items.map((it, i) => (
              <CarouselCard key={it.key} item={it} index={i} testId={testId} grid />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CarouselCard({ item, index, testId, grid = false }) {
  const Wrapper = item.published ? Link : "div";
  const wrapperProps = item.published ? { to: item.to } : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      className={`${grid ? "" : "min-w-[280px] max-w-[320px] sm:min-w-[300px] sm:max-w-[340px] snap-start shrink-0"}`}
    >
      <Wrapper
        {...wrapperProps}
        data-testid={`${testId}-card-${item.key}`}
        className={`group block bg-white border border-slate-200 rounded-xl overflow-hidden h-full ${
          item.published ? "hover:shadow-xl hover:-translate-y-1 hover:border-sky-200" : "opacity-80"
        } transition-all duration-300`}
      >
        <div className="relative w-full aspect-[16/10] bg-slate-100 overflow-hidden">
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <TagIcon className="h-10 w-10" />
            </div>
          )}
          {item.metric && (
            <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-lg px-2.5 py-1 shadow-md">
              <p className="text-base font-extrabold text-sky-600 leading-none">{item.metric}</p>
              {item.metricLabel && (
                <p className="text-[9px] font-medium text-slate-500 uppercase tracking-wider mt-0.5">
                  {item.metricLabel}
                </p>
              )}
            </div>
          )}
          {!item.published && (
            <div className="absolute top-3 left-3 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1 border border-amber-200">
              Coming Soon
            </div>
          )}
        </div>

        <div className="p-4 lg:p-5">
          {item.tag && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-100">
              <TagIcon className="h-2.5 w-2.5" /> {item.tag}
            </span>
          )}
          <h3 className="mt-2.5 text-sm sm:text-base font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-sky-600 transition-colors">
            {item.title}
          </h3>
          {item.excerpt && (
            <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed line-clamp-2">{item.excerpt}</p>
          )}

          <div className="mt-4 flex items-center gap-3 text-[10px] text-slate-400">
            {item.author && (
              <span className="inline-flex items-center gap-1">
                {item.author}
              </span>
            )}
            {item.date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {item.date}
              </span>
            )}
            {item.readTime && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {item.readTime}
              </span>
            )}
          </div>

          {item.published && (
            <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 group-hover:text-sky-700">
              Read story <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          )}
        </div>
      </Wrapper>
    </motion.div>
  );
}
